import { getInitialSelectedVariant } from '@src/components/product-card/product-card.helpers'
import { IProductCard } from '@src/components/product-card/product-card.types'
import { difference, keyBy, takeWhile } from '@src/utils/functions/utils'

import { productContextInitialState, RecommendationUIData } from './ProductPageContext'
import { RECOMMENDED_SECTION_IDS } from './productPageContext.constants'

export const RECOMMENDED_SECTION_LIMIT_UI: Record<RECOMMENDED_SECTION_IDS, number | undefined> = {
  [RECOMMENDED_SECTION_IDS.SLOT1]: undefined, // undefined means not limit is set for this field
  [RECOMMENDED_SECTION_IDS.SLOT2]: 8,
  [RECOMMENDED_SECTION_IDS.SLOT3]: 8,
  [RECOMMENDED_SECTION_IDS.SLOT4]: undefined,
}

export const NEW_RECOMMENDED_SECTION_LIMIT_UI: Record<RECOMMENDED_SECTION_IDS, number | undefined> = {
  [RECOMMENDED_SECTION_IDS.SLOT1]: undefined, // undefined means not limit is set for this field
  [RECOMMENDED_SECTION_IDS.SLOT2]: 12,
  [RECOMMENDED_SECTION_IDS.SLOT3]: 12,
  [RECOMMENDED_SECTION_IDS.SLOT4]: 48,
}

export const NEW_RECOMMENDED_SECTION_LIMIT_UI_MOBILE: Record<RECOMMENDED_SECTION_IDS, number | undefined> = {
  [RECOMMENDED_SECTION_IDS.SLOT1]: undefined, // undefined means not limit is set for this field
  [RECOMMENDED_SECTION_IDS.SLOT2]: 18,
  [RECOMMENDED_SECTION_IDS.SLOT3]: 18,
  [RECOMMENDED_SECTION_IDS.SLOT4]: 48,
}

export const RECOMMENDED_SECTION_PRIORITY_ARRAY = [
  RECOMMENDED_SECTION_IDS.SLOT1,
  RECOMMENDED_SECTION_IDS.SLOT2,
  RECOMMENDED_SECTION_IDS.SLOT3,
  RECOMMENDED_SECTION_IDS.SLOT4,
]

const getFilteredRecommendedProductKeys = (
  sectionKey: RECOMMENDED_SECTION_IDS,
  productCards: IProductCard[] = [],
  recommendationUIdata: RecommendationUIData,
  sectionLimit: Record<RECOMMENDED_SECTION_IDS, number | undefined>,
) => {
  const identifierMappedProductCards = keyBy(productCards, (productCard: IProductCard) => {
    const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
    return `${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`
  })

  const focussedPriority = takeWhile(RECOMMENDED_SECTION_PRIORITY_ARRAY, sectionName => sectionName !== sectionKey)

  const focussedPriorityIdentifiers = focussedPriority.map(priority => {
    if (!Array.isArray(recommendationUIdata[priority].data)) return []
    return recommendationUIdata[priority].data
      .map(pc => {
        const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: pc.product.cardVariants })
        return `${pc.product.productId}_${initialSelectedVariant?.displayConfig.value}`
      })
      .slice(0, sectionLimit[priority])
  })

  const productCardKeys = productCards.map(productCard => {
    const initialSelectedVariant = getInitialSelectedVariant({ cardVariants: productCard.product.cardVariants })
    return `${productCard.product.productId}_${initialSelectedVariant?.displayConfig.value}`
  })

  const filteredIdentifiers = difference(productCardKeys, focussedPriorityIdentifiers.flat())

  //Only return limited items to UI, based on the limit for that module
  return filteredIdentifiers.map(id => identifierMappedProductCards[id]).slice(0, sectionLimit[sectionKey])
}

export const transformedRecommendationData = (
  apiData: RecommendationUIData,
  priorityArray: RECOMMENDED_SECTION_IDS[],
  sectionLimit: Record<RECOMMENDED_SECTION_IDS, number | undefined>,
) => {
  const newRecommendationData = { ...productContextInitialState.recommendationUIData } // init

  for (const key of priorityArray) {
    const data = getFilteredRecommendedProductKeys(key, apiData[key].data, newRecommendationData, sectionLimit)
    newRecommendationData[key] = {
      ...newRecommendationData[key],
      data,
      has_reached_limit: sectionLimit[key] ? data.length >= sectionLimit[key] : false,
    }
  }

  return newRecommendationData
}
